import { getToken } from "../../utils/auth";

// Client of api/crm-postventa-rollo. Same session contract as CRM Postventa V2 (crmPostventaV2Api.js): the
// token travels in the X-CRM-Token header and in the body of POSTs; errors throw an Error with the server
// "mensaje" and the HTTP status (400, 403, 404, 409).
const API_ROOT = "/api/crm-postventa-rollo";

const withTokenHeader = (token) => ({
  "Content-Type": "application/json",
  "X-CRM-Token": token || getToken() || "",
});

const handleResponse = async (response) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.mensaje || payload?.message || "No se pudo completar la operación.");
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });

  const serialized = query.toString();
  return serialized ? `?${serialized}` : "";
};

const getJson = async (path, params) => {
  const token = getToken() || "";
  const response = await fetch(`${API_ROOT}${path}${buildQuery(params)}`, {
    method: "GET",
    headers: withTokenHeader(token),
  });

  return handleResponse(response);
};

// File name of a Content-Disposition header (RFC 5987 filename* first, then filename), or null.
const fileNameOf = (disposition) => {
  const value = String(disposition || "");
  const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/.exec(value);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ""));
    } catch (decodeError) {
      // Malformed encoding: fall back to the plain filename.
    }
  }
  const plain = /filename\s*=\s*("([^"]+)"|[^;]+)/.exec(value);
  const name = plain ? (plain[2] || plain[1]).trim() : "";
  return name || null;
};

const getBlob = async (path, params) => {
  const token = getToken() || "";
  const response = await fetch(`${API_ROOT}${path}${buildQuery(params)}`, {
    method: "GET",
    headers: withTokenHeader(token),
  });
  if (!response.ok) return handleResponse(response);
  return response;
};

const postJson = async (path, body = {}) => {
  const token = getToken() || "";
  const response = await fetch(`${API_ROOT}${path}`, {
    method: "POST",
    headers: withTokenHeader(token),
    body: JSON.stringify({
      token,
      ...body,
    }),
  });

  return handleResponse(response);
};

export const crmPostventaRolloApi = {
  // GET gestiones?vista=activas|finalizadas&texto&estado&prioridad&responsableUsuarioId(0 = sin asignar)
  // &provincia&origen&resultado&cierreDesde&cierreHasta&pagina&tamanoPagina
  listarGestiones: async (params = {}) => getJson("/gestiones", params),
  // { gestion (same shape as a queue row plus the detail fields), timeline, entregasAnteriores, ... }
  obtenerGestion: async (id) => getJson(`/gestiones/${id}`),
  // .xlsx of Finalizadas with the list filters (no page). The server caps the rows and says so in
  // X-Rollo-Export-Total (rows matching the filters), X-Rollo-Export-Rows (rows written to the file) and
  // X-Rollo-Export-Truncated ("true" when it cut them).
  // Answers { blob, fileName (from Content-Disposition, or null), total (or null), rows (or null), truncated }.
  exportarFinalizadas: async (params = {}) => {
    const response = await getBlob("/gestiones/exportar", params);
    const header = (name) => response.headers?.get?.(name) ?? null;
    const count = (name) => {
      const value = Number.parseInt(header(name), 10);
      return Number.isNaN(value) ? null : value;
    };
    return {
      blob: await response.blob(),
      fileName: fileNameOf(header("Content-Disposition")),
      total: count("X-Rollo-Export-Total"),
      rows: count("X-Rollo-Export-Rows"),
      truncated: String(header("X-Rollo-Export-Truncated")).toLowerCase() === "true",
    };
  },
  // { cliente, terminales, prefill, ultimaEntrega, ultimasGestiones, gestionAbiertaId } of one alta (clienteId).
  obtenerContexto: async (clienteId) => getJson("/contexto", { clienteId }),
  // body: { clienteId, numeroTerminal|null, canalPedido, cantidadSolicitada, sinRollos, direccionEntrega,
  // contactoNombre, contactoTelefono, fechaEstimadaEntrega, observacion, correlationId }. A 409 for an open
  // gestión carries gestionAbiertaId in error.payload.
  crearGestion: async (body = {}) => postJson("/gestiones", body),
  // body: { accion: PREPARAR|ENVIAR|ENTREGAR|NO_ENTREGADO|CANCELAR, observacion, cadete, fechaSalida, fechaEntrega,
  // recibioNombre, cantidadEntregada, motivo, nuevaFechaEstimada, versionToken, correlationId }. A stale
  // versionToken answers 409.
  ejecutarAccion: async (id, body = {}) => postJson(`/gestiones/${id}/acciones`, body),
  tomar: async (id, { versionToken } = {}) => postJson(`/gestiones/${id}/tomar`, { versionToken }),
  asignar: async (id, { usuarioId, versionToken } = {}) => postJson(`/gestiones/${id}/asignar`, { usuarioId, versionToken }),
  liberar: async (id, { versionToken } = {}) => postJson(`/gestiones/${id}/liberar`, { versionToken }),
};
